/**
 * MeasureTool — Distance and bearing measurement overlay
 *
 * When active, captures two map clicks (point A and point B), draws a
 * dashed line between them, and displays distance/bearing information
 * at the midpoint and in a results panel.
 */

import React, { useState, useCallback, useEffect } from 'react'
import { Polyline, useMapEvents } from 'react-leaflet'
import { latLngToMgrs, formatMgrsDisplay } from '../utils/mgrsConvert.js'
import {
  haversineDistance,
  calculateBearing,
  formatDistance,
  formatBearing,
} from '../utils/measureCalc.js'

/**
 * Internal component that captures map clicks for measurement points.
 */
function MeasureClickHandler({ onClickPoint }) {
  useMapEvents({
    click(e) {
      onClickPoint({ lat: e.latlng.lat, lng: e.latlng.lng })
    },
  })
  return null
}

/**
 * Format a point's MGRS, returning '---' on conversion failure.
 */
function safeMgrs(lat, lng) {
  try {
    return formatMgrsDisplay(latLngToMgrs(lat, lng, 3))
  } catch {
    return '---'
  }
}

/**
 * MeasureTool component.
 *
 * @param {Object} props
 * @param {boolean} props.active - Whether the tool is in measuring mode
 * @param {function} [props.onMeasure] - Callback with measurement result data
 */
export default function MeasureTool({ active, onMeasure }) {
  const [pointA, setPointA] = useState(null)
  const [pointB, setPointB] = useState(null)

  // Reset points when tool is deactivated
  useEffect(() => {
    if (!active) {
      setPointA(null)
      setPointB(null)
    }
  }, [active])

  const handleClickPoint = useCallback(
    (point) => {
      if (!pointA) {
        setPointA(point)
      } else if (!pointB) {
        setPointB(point)
        // Fire measurement callback
        if (onMeasure) {
          const dist = haversineDistance(
            pointA.lat,
            pointA.lng,
            point.lat,
            point.lng
          )
          const bearing = calculateBearing(
            pointA.lat,
            pointA.lng,
            point.lat,
            point.lng
          )
          onMeasure({
            pointA,
            pointB: point,
            distanceMeters: dist,
            bearingDegrees: bearing,
          })
        }
      }
    },
    [pointA, pointB, onMeasure]
  )

  const handleClear = useCallback(() => {
    setPointA(null)
    setPointB(null)
  }, [])

  if (!active) {
    return null
  }

  const hasBothPoints = pointA && pointB
  let dist, bearing, midpoint

  if (hasBothPoints) {
    dist = haversineDistance(pointA.lat, pointA.lng, pointB.lat, pointB.lng)
    bearing = calculateBearing(pointA.lat, pointA.lng, pointB.lat, pointB.lng)
    midpoint = {
      lat: (pointA.lat + pointB.lat) / 2,
      lng: (pointA.lng + pointB.lng) / 2,
    }
  }

  return (
    <>
      {/* Capture clicks when we still need points */}
      {!hasBothPoints && <MeasureClickHandler onClickPoint={handleClickPoint} />}

      {/* Dashed line between A and B */}
      {hasBothPoints && (
        <Polyline
          positions={[
            [pointA.lat, pointA.lng],
            [pointB.lat, pointB.lng],
          ]}
          pathOptions={{
            color: '#c8a415',
            weight: 2,
            dashArray: '8 6',
            className: 'measure-line',
          }}
        />
      )}

      {/* Midpoint label */}
      {hasBothPoints && midpoint && (
        <div
          className="measure-label"
          style={{
            position: 'absolute',
            pointerEvents: 'none',
          }}
        >
          {formatDistance(dist)} | {formatBearing(bearing)}
        </div>
      )}

      {/* Results panel */}
      {hasBothPoints && (
        <div className="measure-panel" data-testid="measure-panel">
          <div className="info-title">Measurement</div>

          <div className="measure-panel-row">
            <span className="coord-label">Distance</span>
            <span className="coord-value">{formatDistance(dist)}</span>
          </div>
          <div className="measure-panel-row">
            <span className="coord-label">Imperial</span>
            <span className="coord-value">
              {formatDistance(dist, { imperial: true })}
            </span>
          </div>
          <div className="measure-panel-row">
            <span className="coord-label">Bearing</span>
            <span className="coord-value">{formatBearing(bearing)}</span>
          </div>

          <div className="measure-panel-divider" />

          <div className="measure-panel-row">
            <span className="coord-label">Point A</span>
            <span className="mgrs-text">
              {safeMgrs(pointA.lat, pointA.lng)}
            </span>
          </div>
          <div className="measure-panel-row">
            <span className="coord-label">Point B</span>
            <span className="mgrs-text">
              {safeMgrs(pointB.lat, pointB.lng)}
            </span>
          </div>

          <button
            className="btn-military measure-clear-btn"
            onClick={handleClear}
            type="button"
          >
            Clear
          </button>
        </div>
      )}
    </>
  )
}
